import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CustomTooltip from 'common/customTooltip';
import BookmarkIcon from 'icons/BookmarkIcon';
import GradientSparklesIcon from 'icons/GradientSparklesIcon';
import { IconExternalLink } from '@tabler/icons-react';
import { store } from '../../../store/store';
import { useSelector } from 'react-redux';
import * as FavoriteReportActions from '../../frontoffice/favoriteReport/actions/action';
import { AppConfig } from 'AppConfig';
import { showErrorToast } from 'utils/showErrorToast';

const quickViewOutlineGradient = 'linear-gradient(135deg, #C6D41F 0%, #068CF2 45.988%, #2E54A6 100%)';

const ReportOperation = ({ card, handleReportClick, handleShareClick, shouldShowFavorite = true, hasQuickView = false, handleQuickViewClick, containerPx = '12px', compact = false, leftActionGap = '8px', disabled = false }) => {
  const favoriteReports = useSelector((state) => state.favoriteReport?.favoriteReports);
  const cardId = String(card?.id ?? card?.scId ?? '');
  const isFavorited = Array.isArray(favoriteReports)
    ? favoriteReports.some((report) => String(report?.id ?? report?.scId ?? '') === cardId)
    : !!card?.favorited;

  const handleKeyDown = (e, action) => {
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

   const toggleFavorite = async (card) => {

    if (!card || card.isPlaceholder) return;
    const previousFavorited = isFavorited;
    const isAdHoc = card.category?.startsWith('adHoc') || card.category === AppConfig.REPORT_CATEGORY?.AD_HOC?.code;
    const effectiveType = isAdHoc ? AppConfig.AD_HOC_CAROUSEL : card.category;

    try {
      const action = previousFavorited
        ? FavoriteReportActions.deleteFavoriteReport(card.id, effectiveType, card.category, false)
        : FavoriteReportActions.addFavoriteReport(card.id, effectiveType, false);

      await store.dispatch(action);
      await store.dispatch(FavoriteReportActions.getFavoriteReport());
      return;
    } catch (err) {
       const statusCode = err?.response?.status ?? err?.status;
       if(previousFavorited) {
          showErrorToast("Unsave Failed" , "Unable to un-save report. Please try again.", "455px" , "355px" , "#88041E" , true);
       }else{
         showErrorToast("Save Failed" , "Unable to save report. Please try again.", "455px" , "355px" , "#88041E" , true);
       }
       console.error('Error toggling favorite:', statusCode ? `HTTP ${statusCode}` : err);
       return;
     }
  };

  return (
    <Box
      sx={{
        paddingLeft: containerPx,
        width: compact ? 'auto' : '100%',
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: compact ? 'flex-start' : 'space-between',
        gap: compact ? '24px' : 0,
        flexShrink: 0
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: leftActionGap }}>
        {shouldShowFavorite && (
          <CustomTooltip
            title={isFavorited ? 'Unsave' : 'Save'}
            arrow
            padding="8px 12px"
            borderRadius="8px 0px 8px 8px"
            width={isFavorited ? '81px' : '63px'}
            height="42px"
            popperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [-9, -11]
                  }
                }
              ]
            }}>
            <IconButton
              aria-label={isFavorited ? 'unfavorite card' : 'favorite card'}
              disabled={disabled}
              size="small"
              onClick={() => {
                  toggleFavorite(card);
              }}
              onKeyDown={(e) =>
                handleKeyDown(e, () => {
                  toggleFavorite(card);
                })
              }
              sx={{ p: 0, width: 20, height: 20 }}
              tabIndex={0}>
              <BookmarkIcon filled={isFavorited} />
            </IconButton>
          </CustomTooltip>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
        {hasQuickView && (
          <Button
            variant="outlined"
            disabled={disabled}
            sx={{
              display: 'flex',
              height: '28px',
              padding: '8px 10px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
              borderRadius: '24px',
              border: '1px solid transparent',
              background: `linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 100%) padding-box, ${quickViewOutlineGradient} border-box`,
              textTransform: 'none',
              color: '#2F54A5',
              fontFamily: '"Noto Sans"',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '150%',
              minWidth: 'unset',
              '&:hover': {
                background: '#D8E6FF',
                borderColor: '#001A47',
                color: '#001A47',
              },
            }}
            onClick={() => handleQuickViewClick?.(card)}
            onKeyDown={(e) => handleKeyDown(e, () => handleQuickViewClick?.(card))}
            tabIndex={0}>
            <GradientSparklesIcon size={12} />
            Quick View
          </Button>
        )}
        <Button
          variant="outlined"
          disabled={disabled}
          sx={{
            display: 'flex',
            height: '28px',
            padding: '8px 10px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '5px',
            borderRadius: '24px',
            border: '1px solid #2F54A5',
            background: '#FFF',
            textTransform: 'none',
            color: '#2F54A5',
            fontFamily: '"Noto Sans"',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '150%',
            minWidth: 'unset',
            '&:hover': {
              backgroundColor: '#D8E6FF',
              borderColor: '#001A47',
              color: '#001A47',
            },
          }}
          onClick={() => handleReportClick(card)}
          onKeyDown={(e) => handleKeyDown(e, () => handleReportClick(card))}
          tabIndex={0}>
          <IconExternalLink size={12} color="currentColor" />
          Launch
        </Button>
      </Box>
    </Box>
  );
};

export default ReportOperation;